// Application monitoring and error tracking
// Note: supabase is lazy-loaded to avoid circular dependencies

// Error tracking and reporting
class ErrorTracker {
  constructor() {
    this.errors = [];
    this.maxErrors = 100;
  }

  logError(error, context = {}) {
    const errorLog = {
      id: Date.now().toString(),
      message: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      context
    };

    this.errors.unshift(errorLog);
    if (this.errors.length > this.maxErrors) {
      this.errors = this.errors.slice(0, this.maxErrors);
    }

    // Log to console in development
    if (import.meta.env.DEV) {
      console.error('Error tracked:', errorLog);
    }

    // Send to monitoring service in production
    this.sendToMonitoring(errorLog);
  }

  async sendToMonitoring(errorLog) {
    try {
      // Lazy import to avoid circular dependency
      const { supabase } = await import('./supabase');

      // In production, you would send to a service like Sentry
      // For now, we'll log to Supabase
      await supabase.from('error_logs').insert({
        error_message: errorLog.message,
        error_stack: errorLog.stack,
        url: errorLog.url,
        user_agent: errorLog.userAgent,
        context: errorLog.context,
        created_at: errorLog.timestamp
      });
    } catch (err) {
      console.error('Failed to send error to monitoring:', err);
    }
  }

  getErrors() {
    return this.errors;
  }

  clearErrors() {
    this.errors = [];
  }
}

export const errorTracker = new ErrorTracker();

// Performance monitoring
class PerformanceMonitor {
  constructor() {
    this.metrics = new Map();
  }

  startTimer(name) {
    this.metrics.set(name, performance.now());
  }

  endTimer(name) {
    const startTime = this.metrics.get(name);
    if (startTime) {
      const duration = performance.now() - startTime;
      this.metrics.delete(name);
      
      // Log slow operations
      if (duration > 1000) {
        console.warn(`Slow operation: ${name} took ${duration.toFixed(2)}ms`);
      }
      
      return duration;
    }
    return 0;
  }

  measureAsync(name, asyncFn) {
    return async (...args) => {
      this.startTimer(name);
      try {
        const result = await asyncFn(...args);
        this.endTimer(name);
        return result;
      } catch (error) {
        this.endTimer(name);
        errorTracker.logError(error, { operation: name, args });
        throw error;
      }
    };
  }
}

export const performanceMonitor = new PerformanceMonitor();

// Health check utilities
export const healthChecks = {
  async database() {
    try {
      const { supabase } = await import('./supabase');
      const { data, error } = await supabase
        .from('universities')
        .select('count')
        .limit(1);

      return { status: 'healthy', service: 'database' };
    } catch (error) {
      return { status: 'unhealthy', service: 'database', error: error.message };
    }
  },

  async authentication() {
    try {
      const { supabase } = await import('./supabase');
      const { data: { session } } = await supabase.auth.getSession();
      return { status: 'healthy', service: 'authentication' };
    } catch (error) {
      return { status: 'unhealthy', service: 'authentication', error: error.message };
    }
  },

  async storage() {
    try {
      const { supabase } = await import('./supabase');
      const { data, error } = await supabase.storage.listBuckets();
      return { status: 'healthy', service: 'storage' };
    } catch (error) {
      return { status: 'unhealthy', service: 'storage', error: error.message };
    }
  }
};

// Global error handler
export const setupGlobalErrorHandling = () => {
  // Catch unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    errorTracker.logError(new Error(event.reason), { type: 'unhandledrejection' });
  });

  // Catch JavaScript errors
  window.addEventListener('error', (event) => {
    errorTracker.logError(event.error, { 
      type: 'javascript',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno
    });
  });
};

// Resource monitoring
export const monitorResources = () => {
  if ('memory' in performance) {
    const memory = performance.memory;
    const memoryUsage = {
      used: Math.round(memory.usedJSHeapSize / 1048576), // MB
      total: Math.round(memory.totalJSHeapSize / 1048576), // MB
      limit: Math.round(memory.jsHeapSizeLimit / 1048576) // MB
    };
    
    // Warn if memory usage is high
    if (memoryUsage.used / memoryUsage.limit > 0.8) {
      console.warn('High memory usage detected:', memoryUsage);
    }
    
    return memoryUsage;
  }
  return null;
};